import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from './keys.ts'

/**
 * Profile photos.
 *
 * Three steps, and the middle one does not touch the API: ask for a presigned
 * URL, PUT the bytes straight to storage, then tell the API it landed. A photo
 * has no business travelling through the application server, and the upload
 * keeps working while the API is busy.
 */

export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export type PhotoContentType = (typeof ALLOWED_PHOTO_TYPES)[number]

/** Where the photo is served from. The API proxies it, so the session applies. */
export const personPhotoUrl = (personId: string, version?: string | null) =>
  `/api/v1/people/${personId}/photo${version ? `?v=${encodeURIComponent(version)}` : ''}`

export function useUploadPersonPhoto(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: { type: string; body: Blob | ArrayBuffer }) => {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type as PhotoContentType)) {
        throw new Error('Photos must be JPEG, PNG or WebP.')
      }

      const { data, error } = await api.api.v1.people({ id: personId }).photo['upload-url'].post({
        contentType: file.type as PhotoContentType,
        sizeBytes: file.body instanceof Blob ? file.body.size : file.body.byteLength,
      })
      if (error) throw error

      const uploaded = await fetch(data.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file.body,
      })
      if (!uploaded.ok) throw new Error(`Storage rejected the upload (${uploaded.status}).`)

      const confirmed = await api.api.v1.people({ id: personId }).photo.put({ key: data.key })
      if (confirmed.error) throw confirmed.error
      return confirmed.data
    },
    onSuccess: () => {
      // The photo URL does not change when the photo does, so the person query
      // carries the new key and the `?v=` on the image URL busts the cache.
      void queryClient.invalidateQueries({ queryKey: peopleKeys.detail(personId) })
      void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() })
    },
  })
}

export function useRemovePersonPhoto(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.people({ id: personId }).photo.delete()
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: peopleKeys.detail(personId) })
      void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() })
    },
  })
}
